import React, { useState } from "react";
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Typography, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Header";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useGetWithdrawalsQuery, useVerifyWithdrawalMutation } from "../../../state/api";

const STATUS_COLORS = {
  pending: "#ffda85",
  approved: "#00c9a7",
  rejected: "#de4437",
  cancelled: "#7a7f9d",
};

const statusColor = (status) => STATUS_COLORS[status] || "#7a7f9d";

const payoutAccountLabel = (row) => {
  const details = row.payoutDetails || {};
  if (row.payoutMethod === "bank") {
    return `${details.bankName || "—"} · ${details.accountNumber || "—"}`;
  }
  return details.walletId || "—";
};

const Withdrawals = () => {
  const theme = useTheme();
  const [statusFilter, setStatusFilter] = useState("pending");
  const [pageSize, setPageSize] = useState(20);
  const { data, isLoading, isFetching } = useGetWithdrawalsQuery(
    statusFilter === "all" ? undefined : statusFilter
  );
  const [verifyWithdrawal] = useVerifyWithdrawalMutation();

  const rowData = (data && data.data) || [];

  const handleDecision = async (row, status) => {
    const isReject = status === "rejected";

    let cancellationReason;
    if (isReject) {
      const { value, isConfirmed } = await Swal.fire({
        title: "Reject withdrawal request",
        input: "text",
        inputLabel: "Reason for rejection",
        inputPlaceholder: "e.g. Invalid account details",
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#de4437",
      });
      if (!isConfirmed) return;
      cancellationReason = value;
    } else {
      const { isConfirmed } = await Swal.fire({
        title: "Approve withdrawal request",
        html: `Approve payout of <b>NRs ${row.amount.toLocaleString()}</b> to <b>${payoutAccountLabel(row)}</b>?<br/><small>This calls the payout provider immediately.</small>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Approve",
        confirmButtonColor: "#00c9a7",
      });
      if (!isConfirmed) return;
    }

    try {
      const response = await verifyWithdrawal({
        requestId: row._id,
        status,
        cancellationReason,
      }).unwrap();
      toast.success(response.message || "Withdrawal request updated");
    } catch (error) {
      const message =
        error?.data?.message || "Payout provider declined this payout";
      toast.error(message);
    }
  };

  const columns = [
    {
      field: "user",
      headerName: "User",
      flex: 0.8,
      renderCell: (params) => {
        const user = params.row.userId;
        if (!user || typeof user !== "object") return "—";
        return (
          <Box>
            <Box sx={{ fontSize: "0.85rem" }}>{user.fullname}</Box>
            <Box sx={{ fontSize: "0.7rem", opacity: 0.7 }}>
              {user.email} {user.mobileNumber ? `· ${user.mobileNumber}` : ""}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.5,
      renderCell: (params) => `NRs ${Number(params.value).toLocaleString()}`,
    },
    {
      field: "payoutMethod",
      headerName: "Method",
      flex: 0.4,
      renderCell: (params) => (
        <Typography sx={{ textTransform: "capitalize", fontSize: "0.85rem" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "payoutAccount",
      headerName: "Payout Account",
      flex: 0.9,
      renderCell: (params) => payoutAccountLabel(params.row),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 0.8,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          sx={{
            textTransform: "capitalize",
            backgroundColor: `${statusColor(params.value)}22`,
            color: statusColor(params.value),
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: "providerTransactionId",
      headerName: "Provider Ref",
      flex: 0.6,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "createdAt",
      headerName: "Requested",
      flex: 0.5,
      renderCell: (params) => format(new Date(params.value), "yyyy-MM-dd HH:mm"),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.6,
      renderCell: (params) =>
        params.row.status === "pending" ? (
          <Box display="flex" gap="6px">
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => handleDecision(params.row, "approved")}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleDecision(params.row, "rejected")}
            >
              Reject
            </Button>
          </Box>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <ToastContainer />
      <Header
        title="WITHDRAWAL REQUESTS"
        subtitle="Review and settle user cash-out requests"
      />

      <Box mt="20px" display="flex" alignItems="center" gap="1rem">
        <FormControl variant="standard" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box
        mt="20px"
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.background.alt,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
        }}
      >
        <DataGrid
          loading={isLoading || isFetching}
          getRowId={(row) => row._id}
          rows={rowData}
          columns={columns}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Box>
    </Box>
  );
};

export default Withdrawals;
